import { ArrowLeft, Home, LayoutGrid, MapPin, Monitor } from "lucide-react";
import useSWR from "swr";

import type {
  BenchRoomRow,
  BenchRow,
  HighCourtRow,
  JudgeOption,
} from "./types";
import {
  EmptyRow,
  IconBox,
  LoadingRow,
  MeetingDetails,
  MobileCardList,
  MobileInfoRow,
  PrimaryButton,
  ResponsiveTableShell,
  RowActions,
  StatusPill,
} from "./SharedUi";

interface CourtHallsProps {
  highCourtId: string;
  benchId: string;
  onBack: () => void;
  onAdd: (highCourt: HighCourtRow, bench: BenchRow) => void;
  onEdit: (
    room: BenchRoomRow,
    highCourt: HighCourtRow,
    bench: BenchRow,
  ) => void;
  onDelete: (room: BenchRoomRow) => void;
}

const getFloorLabel = (floor?: string) => {
  if (!floor) return "N/A";
  const numericFloor = Number(floor);

  if (Number.isNaN(numericFloor)) return floor;
  if (numericFloor === 0) return "Ground Floor";

  const suffixes = ["th", "st", "nd", "rd"];
  const value = numericFloor % 100;

  return `${numericFloor}${
    suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]
  } Floor`;
};

const judgeName = (
  judge: NonNullable<BenchRoomRow["judges"]>[number],
  judgeLabelById: Record<string, string>,
) => {
  if (typeof judge === "string") return judgeLabelById[judge] || "";

  return (
    judge.label ||
    judge.judgeName ||
    judge.fullName ||
    [judge.firstName, judge.lastName].filter(Boolean).join(" ") ||
    judge.name ||
    "Assigned Judge"
  );
};

export const CourtHalls = ({
  highCourtId,
  benchId,
  onBack,
  onAdd,
  onEdit,
  onDelete,
}: CourtHallsProps) => {
  const { data: highCourtResponse, isLoading: highCourtsLoading } = useSWR(
    "/api/court/get-highcourt",
  );
  const { data: judgesResponse } = useSWR("/api/bench/get-highcourt-judges");
  const judges = (judgesResponse?.data || []) as JudgeOption[];
  const judgeLabelById = Object.fromEntries(
    judges.map((judge) => [String(judge.value), judge.label]),
  );
  const highCourts = (highCourtResponse?.data || []) as HighCourtRow[];
  const highCourt = highCourts.find((court) => court._id === highCourtId);
  const { data: benchResponse, isLoading: benchesLoading } = useSWR(
    highCourtId
      ? `/api/court/get-benches/${highCourtId}?page=1&limit=1000`
      : null,
  );
  const benches = ((benchResponse?.status === false
    ? []
    : benchResponse?.data) || []) as BenchRow[];
  const bench = benches.find((item) => item._id === benchId);
  const { data: benchRoomResponse, isLoading: benchRoomsLoading } = useSWR(
    benchId ? `/api/court/get-bench-rooms/${benchId}?page=1&limit=1000` : null,
  );
  const benchRooms = ((benchRoomResponse?.status === false
    ? []
    : benchRoomResponse?.data) || []) as BenchRoomRow[];
  const isLoading = highCourtsLoading || benchesLoading || benchRoomsLoading;

  if (!highCourt || !bench) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        {isLoading
          ? "Loading court hall details..."
          : "Court hall parent not found"}
      </div>
    );
  }
  console.log(benchRooms);

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 text-blue-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {highCourt.highCourtName}
      </button>

      <section className="flex flex-col gap-5 rounded-xl border border-slate-200 bg-white p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
          <IconBox icon={<LayoutGrid className="h-7 w-7" />} large />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="min-w-0 text-xl font-bold text-slate-950">
                {bench.benchName}
              </h1>
              <StatusPill status={bench.status || "active"} />
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {bench.location || highCourt.capitalCity}, {highCourt.stateName}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Home className="h-4 w-4" />
                {highCourt.highCourtName}
              </span>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 px-8 py-4 text-center">
          <div className="text-2xl font-bold text-violet-600">
            {benchRooms.length}
          </div>
          <div className="text-xs text-slate-500">Court Halls</div>
        </div>
      </section>

      <div className="mb-7 mt-9 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-slate-600">
          Court halls under{" "}
          <span className="font-semibold text-slate-900">
            {bench.benchName}
          </span>
          . Manage halls, assign judges, and configure facilities.
        </p>
        <PrimaryButton
          label="Add Court Hall"
          onClick={() => onAdd(highCourt, bench)}
          className="w-full sm:w-auto"
        />
      </div>

      <ResponsiveTableShell>
        <table className="w-full min-w-[1020px]  table-fixed">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Hall Details</th>
              <th className="w-28  py-4">Judge Count</th>
              <th className="px-6 py-4">Judges Assigned</th>
              <th className="px-6 py-4">Facilities</th>
              <th className="px-6 py-4">Meeting Details</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <LoadingRow colSpan={6} />}
            {benchRooms.length ? (
              benchRooms.map((room) => {
                const judgeLabels = (room.judges || [])
                  .map((judge) => judgeName(judge, judgeLabelById))
                  .filter(Boolean);
                const hiddenJudgeCount = Math.max(judgeLabels.length - 2, 0);
                const hiddenFacilityCount = Math.max(
                  (room.facilities?.length || 0) - 3,
                  0,
                );

                return (
                  <tr key={room._id} className="hover:bg-slate-50">
                    <td className="px-6 py-5">
                      <div className="font-semibold">{room.benchName}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-slate-400">
                        <Monitor className="h-3.5 w-3.5" />
                        <span>Room {room.courtRoom}</span>
                        <span>·</span>
                        <span>{getFloorLabel(room.floorNumber)}</span>
                      </div>
                    </td>
                    <td className="py-5 text-center font-semibold">
                      {room.judgesNumber}
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-wrap gap-1.5">
                        {judgeLabels.length ? (
                          <>
                            {judgeLabels.slice(0, 2).map((judge) => (
                              <span
                                key={judge}
                                className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
                              >
                                {judge}
                              </span>
                            ))}
                            {hiddenJudgeCount > 0 && (
                              <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                                +{hiddenJudgeCount}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-slate-400">
                            {room.judges?.length || 0} judges assigned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-wrap gap-1.5">
                        {room.facilities?.length ? (
                          <>
                            {room.facilities.slice(0, 3).map((facility) => (
                              <span
                                key={facility}
                                className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
                              >
                                {facility}
                              </span>
                            ))}
                            {hiddenFacilityCount > 0 && (
                              <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                                +{hiddenFacilityCount}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-slate-400">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <MeetingDetails
                        meetingId={room?.meetingId}
                        meetingPassword={room?.meetingPassword}
                        meetingToken={room?.meetingToken}
                      />
                    </td>
                    <td className="px-6 py-5">
                      <StatusPill status={room.status} />
                    </td>
                    <td className="px-6 py-5">
                      <RowActions
                        onEdit={() => onEdit(room, highCourt, bench)}
                        onDelete={() => onDelete(room)}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <EmptyRow
                colSpan={6}
                label="Add court halls to this bench"
                actionLabel="Add Court Hall"
                onAction={() => onAdd(highCourt, bench)}
              />
            )}
          </tbody>
        </table>
      </ResponsiveTableShell>

      <MobileCardList
        isLoading={isLoading}
        isEmpty={benchRooms.length === 0}
        emptyLabel="Add court halls to this bench"
        actionLabel="Add Court Hall"
        onAction={() => onAdd(highCourt, bench)}
      >
        {benchRooms.map((room) => {
          const judgeLabels = (room.judges || [])
            .map((judge) => judgeName(judge, judgeLabelById))
            .filter(Boolean);
          const hiddenJudgeCount = Math.max(judgeLabels.length - 2, 0);
          const hiddenFacilityCount = Math.max(
            (room.facilities?.length || 0) - 3,
            0,
          );

          return (
            <article
              key={room._id}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-950">
                    {room.benchName}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-slate-400">
                    <Monitor className="h-3.5 w-3.5" />
                    <span>Room {room.courtRoom}</span>
                    <span>·</span>
                    <span>{getFloorLabel(room.floorNumber)}</span>
                  </div>
                </div>
                <RowActions
                  onEdit={() => onEdit(room, highCourt, bench)}
                  onDelete={() => onDelete(room)}
                />
              </div>
              <div className="mt-4 space-y-3">
                <MobileInfoRow label="Judge Count" value={room.judgesNumber} />
                <MobileInfoRow
                  label="Judges Assigned"
                  value={
                    <span className="flex flex-wrap justify-end gap-1.5">
                      {judgeLabels.length ? (
                        <>
                          {judgeLabels.slice(0, 2).map((judge) => (
                            <span
                              key={judge}
                              className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
                            >
                              {judge}
                            </span>
                          ))}
                          {hiddenJudgeCount > 0 && (
                            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                              +{hiddenJudgeCount}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {room.judges?.length || 0} judges assigned
                        </span>
                      )}
                    </span>
                  }
                />
                <MobileInfoRow
                  label="Facilities"
                  value={
                    <span className="flex flex-wrap justify-end gap-1.5">
                      {room.facilities?.length ? (
                        <>
                          {room.facilities.slice(0, 3).map((facility) => (
                            <span
                              key={facility}
                              className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
                            >
                              {facility}
                            </span>
                          ))}
                          {hiddenFacilityCount > 0 && (
                            <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                              +{hiddenFacilityCount}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">N/A</span>
                      )}
                    </span>
                  }
                />
                <MobileInfoRow
                  label="Status"
                  value={<StatusPill status={room.status} />}
                />
              </div>
            </article>
          );
        })}
      </MobileCardList>
    </div>
  );
};
